import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { listUsers } from '../graphql/queries';
import { createMessageRequest } from '../graphql/mutations';
import PaymentGate from '../components/PaymentGate';
import PremiumTeaser from '../components/PremiumTeaser';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

const apiClient = generateClient();

const mockUsers = [
  {
    id: 1,
    name: "Sarah Johnson",
    age: 28,
    location: "London, UK",
    photo: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    compatibility: 95,
    interests: ["Adventure Travel", "Photography", "Local Cuisine"],
    personality: "Extroverted, Organized, Creative",
    bio: "Passionate photographer and adventure seeker. Love exploring hidden gems and capturing authentic moments."
  },
  {
    id: 2,
    name: "Michael Chen",
    age: 32,
    location: "Toronto, Canada",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    compatibility: 88,
    interests: ["Hiking", "Technology", "Cultural Exchange"],
    personality: "Analytical, Friendly, Curious",
    bio: "Tech enthusiast who loves combining modern tools with traditional travel experiences."
  },
  {
    id: 3,
    name: "Emma Rodriguez",
    age: 25,
    location: "Barcelona, Spain",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    compatibility: 92,
    interests: ["Art & Museums", "Language Learning", "Street Food"],
    personality: "Creative, Social, Open-minded",
    bio: "Art lover and language enthusiast. Always excited to learn about new cultures through their art and cuisine."
  },
  {
    id: 4,
    name: "David Kim",
    age: 30,
    location: "Seoul, South Korea",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    compatibility: 85,
    interests: ["K-pop Culture", "Food Tours", "Urban Exploration"],
    personality: "Enthusiastic, Detail-oriented, Helpful",
    bio: "Foodie and culture enthusiast. Love sharing local insights and discovering new flavors together."
  },
  {
    id: 5,
    name: "Lisa Thompson",
    age: 27,
    location: "Melbourne, Australia",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
    compatibility: 90,
    interests: ["Beach Activities", "Yoga", "Eco-tourism"],
    personality: "Calm, Nature-loving, Health-conscious",
    bio: "Yoga instructor and nature enthusiast. Passionate about sustainable travel and wellness experiences."
  },
  {
    id: 6,
    name: "Alex Morgan",
    age: 29,
    location: "Berlin, Germany",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    compatibility: 87,
    interests: ["History", "Music", "Alternative Culture"],
    personality: "Intellectual, Independent, Artistic",
    bio: "History buff and music lover. Fascinated by underground scenes and alternative cultural experiences."
  },
  // Dummy test profiles
  {
    id: 101,
    name: "Test User 1",
    age: 29,
    location: "Manchester, UK",
    photo: "https://randomuser.me/api/portraits/men/32.jpg",
    compatibility: 91,
    interests: ["Football", "Local Pubs", "Live Music"],
    personality: "Adventurous, Outgoing, Social",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Social Adventurer! You love group activities and spontaneous nights out."
  },
  {
    id: 102,
    name: "Test User 2",
    age: 34,
    location: "Berlin, Germany",
    photo: "https://randomuser.me/api/portraits/women/44.jpg",
    compatibility: 87,
    interests: ["Art Galleries", "Cycling", "Coffee Shops"],
    personality: "Creative, Friendly, Curious",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Creative Explorer! You enjoy discovering new places and sharing experiences."
  },
  {
    id: 103,
    name: "Test User 3",
    age: 26,
    location: "Paris, France",
    photo: "https://randomuser.me/api/portraits/men/45.jpg",
    compatibility: 93,
    interests: ["Wine Tasting", "Museums", "Photography"],
    personality: "Thoughtful, Artistic, Calm",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Thoughtful Planner! You appreciate culture and meaningful connections."
  },
  {
    id: 104,
    name: "Test User 4",
    age: 31,
    location: "Barcelona, Spain",
    photo: "https://randomuser.me/api/portraits/women/65.jpg",
    compatibility: 89,
    interests: ["Beach Volleyball", "Tapas", "Dancing"],
    personality: "Energetic, Fun-loving, Sociable",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Fun Seeker! You bring energy and excitement to every group."
  },
  {
    id: 105,
    name: "Test User 5",
    age: 28,
    location: "Rome, Italy",
    photo: "https://randomuser.me/api/portraits/men/51.jpg",
    compatibility: 85,
    interests: ["History", "Cooking", "Walking Tours"],
    personality: "Curious, Warm, Organized",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Cultural Enthusiast! You love learning and sharing stories."
  },
  {
    id: 106,
    name: "Test User 6",
    age: 35,
    location: "Vienna, Austria",
    photo: "https://randomuser.me/api/portraits/women/72.jpg",
    compatibility: 90,
    interests: ["Classical Music", "Coffee Houses", "Hiking"],
    personality: "Intellectual, Calm, Adventurous",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Calm Adventurer! You enjoy both quiet moments and outdoor fun."
  },
  {
    id: 107,
    name: "Test User 7",
    age: 27,
    location: "Amsterdam, Netherlands",
    photo: "https://randomuser.me/api/portraits/men/61.jpg",
    compatibility: 88,
    interests: ["Cycling", "Street Art", "Canal Tours"],
    personality: "Open-minded, Friendly, Active",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re an Urban Explorer! You love discovering hidden gems in the city."
  },
  {
    id: 108,
    name: "Test User 8",
    age: 30,
    location: "Prague, Czech Republic",
    photo: "https://randomuser.me/api/portraits/women/80.jpg",
    compatibility: 92,
    interests: ["Castles", "Jazz Clubs", "Bookshops"],
    personality: "Romantic, Artistic, Curious",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Romantic Explorer! You seek beauty and inspiration everywhere."
  },
  {
    id: 109,
    name: "Test User 9",
    age: 33,
    location: "Dublin, Ireland",
    photo: "https://randomuser.me/api/portraits/men/70.jpg",
    compatibility: 86,
    interests: ["Pubs", "Live Music", "Storytelling"],
    personality: "Witty, Outgoing, Fun",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Social Storyteller! You make every group feel welcome."
  },
  {
    id: 110,
    name: "Test User 10",
    age: 29,
    location: "Zurich, Switzerland",
    photo: "https://randomuser.me/api/portraits/women/90.jpg",
    compatibility: 94,
    interests: ["Hiking", "Chocolate", "Skiing"],
    personality: "Adventurous, Friendly, Reliable",
    testMessage: "This profile is part of site testing. This user does not exist and will not respond to messages.",
    personalityEvaluation: "You’re a Reliable Adventurer! You’re always ready for the next journey."
  },
];

export default function FeedPage() {
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [requested, setRequested] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [region, setRegion] = useState('');
  const [sex, setSex] = useState([]);
  const [interests, setInterests] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [matchScore, setMatchScore] = useState(0);
  const [showPremiumTeaser, setShowPremiumTeaser] = useState(false);
  
  const { subscriptionStatus, isPremium, isFree } = useSubscriptionStatus();

  const allInterests = [
    'Adventure Travel', 'Photography', 'Local Cuisine', 'Hiking', 'Cultural Exchange',
    'Art & Museums', 'Language Learning', 'Street Food', 'K-pop Culture', 'Food Tours',
    'Urban Exploration', 'Beach Activities', 'Yoga', 'Eco-tourism', 'History', 'Music', 'Alternative Culture'
  ];
  const allLanguages = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Korean', 'Chinese', 'Japanese', 'Portuguese'
  ];
  const allSexOptions = ['Male', 'Female', 'Other'];

  function toggleMultiSelect(value, selected, setSelected) {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      const { userId } = await getCurrentUser();
      setCurrentUserId(userId);
      const { data } = await apiClient.graphql({
        query: listUsers,
        variables: { limit: 20 }
      });
      
      if (data.listUsers.items) {
        const filteredUsers = data.listUsers.items.filter(u => u.id !== userId);
        setUsers(filteredUsers);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const handleRequest = async (recipientID) => {
    if (!isPremium) {
      setShowPremiumTeaser(true);
      return;
    }

    try {
      await apiClient.graphql({
        query: createMessageRequest,
        variables: {
          input: {
            requesterID: currentUserId,
            recipientID,
            status: 'pending'
          }
        }
      });
      
      setRequested(prev => new Set([...prev, recipientID]));
      alert('Message request sent!');
    } catch (err) {
      console.error('Error sending request:', err);
      alert('Failed to send request. Please try again.');
    }
  };

  const handleSearch = () => {
    // Mock search results - in production this would query the database
    const mockResults = [
      {
        id: 1,
        name: "Rebecca May",
        age: 26,
        location: "Cardiff, UK",
        photo: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
        matchScore: 95,
        isDummyProfile: true
      },
      {
        id: 2,
        name: "Sarah Johnson",
        age: 28,
        location: "London, UK",
        photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face",
        matchScore: 87,
        isDummyProfile: false
      }
    ];
    
    setSearchResults(mockResults);
  };

  const filteredUsers = mockUsers.filter(user => {
    const matchesAge = (!ageMin || user.age >= parseInt(ageMin)) && (!ageMax || user.age <= parseInt(ageMax));
    const matchesRegion = !region || (user.location && user.location.toLowerCase().includes(region.toLowerCase()));
    const userSex = user.id % 2 === 0 ? 'Male' : 'Female';
    const matchesSex = sex.length === 0 || sex.includes(userSex);
    const matchesInterests = interests.length === 0 || interests.some(i => user.interests.includes(i));
    const userLanguages = allLanguages.filter((_, idx) => (user.id + idx) % 3 === 0);
    const matchesLanguages = languages.length === 0 || languages.some(l => userLanguages.includes(l));
    const matchesMatchScore = !matchScore || user.compatibility >= matchScore;
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.interests.some(interest => interest.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesAge && matchesRegion && matchesSex && matchesInterests && matchesLanguages && matchesMatchScore && matchesSearch;
  });

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2em', marginBottom: 'var(--spacing-4)' }}>⏳</div>
          <h2>Loading Travel Companions...</h2>
          <p>Finding the perfect matches for you</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2em', marginBottom: 'var(--spacing-4)' }}>❌</div>
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button onClick={fetchUsers} className="btn btn--primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
      {/* Premium Teaser for Free Users */}
      {isFree && (
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          color: 'white',
          textAlign: 'center',
          marginBottom: 'var(--spacing-6)'
        }}>
          <div style={{ fontSize: '3em', marginBottom: 'var(--spacing-3)' }}>👥</div>
          <h2 style={{ marginBottom: 'var(--spacing-3)' }}>Discover Your Perfect Travel Companions</h2>
          <p style={{ marginBottom: 'var(--spacing-4)', opacity: 0.9 }}>
            Upgrade to Premium to browse thousands of verified travelers and find your ideal match
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/payment" className="btn btn--primary" style={{
              background: '#4ade80',
              border: 'none',
              color: 'white',
              padding: 'var(--spacing-3) var(--spacing-6)',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}>
              Start Free Trial
            </Link>
            <Link to="/pricing" className="btn btn--secondary" style={{
              background: 'transparent',
              border: '2px solid white',
              color: 'white',
              padding: 'var(--spacing-3) var(--spacing-6)',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}>
              View Plans
            </Link>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <h1 style={{ marginBottom: 'var(--spacing-3)' }}>
          {isPremium ? 'Discover Travel Companions' : 'Preview - Travel Companions'}
        </h1>
        <p style={{ color: 'var(--gray-600)' }}>
          {isPremium 
            ? 'Browse and connect with verified travelers who match your personality and travel style'
            : 'Upgrade to Premium to see all available travelers and start connecting'
          }
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card p-4" style={{ marginBottom: 'var(--spacing-6)' }}>
        <div className="row" style={{ gap: 'var(--spacing-4)' }}>
          <div className="col col--4">
            <input
              type="text"
              placeholder="Search by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: 'var(--spacing-2)' }}
            />
          </div>
          <div className="col col--4">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              style={{ width: '100%', padding: 'var(--spacing-2)' }}
            >
              <option value="all">All Travelers</option>
              <option value="adventure">Adventure Seekers</option>
              <option value="cultural">Cultural Enthusiasts</option>
              <option value="relaxation">Relaxation Seekers</option>
              <option value="food">Food & Wine Lovers</option>
            </select>
          </div>
          <div className="col col--4">
            <button 
              onClick={() => {
                if (!isPremium) {
                  setShowPremiumTeaser(true);
                } else {
                  // Perform advanced search
                  console.log('Advanced search functionality');
                }
              }}
              className="btn btn--primary"
              style={{ width: '100%' }}
            >
              {isPremium ? 'Advanced Search' : 'Upgrade for Advanced Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="row" style={{ gap: 'var(--spacing-4)' }}>
        {(isPremium ? users : users.slice(0, 3)).map((user) => (
          <div key={user.id} className="col col--4">
            <div className="card p-4" style={{ textAlign: 'center' }}>
              <img
                src={user.photo}
                alt={user.name}
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  margin: '0 auto var(--spacing-3)',
                  border: '3px solid var(--primary-blue)'
                }}
              />
              <h3 style={{ marginBottom: 'var(--spacing-2)' }}>{user.name}</h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--spacing-2)' }}>
                {user.age} • {user.location}
              </p>
              <div style={{ 
                background: 'var(--success-green)', 
                color: 'white', 
                padding: 'var(--spacing-1) var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--spacing-3)',
                fontSize: 'var(--font-size-sm)'
              }}>
                {user.compatibility}% Match
              </div>
              <p style={{ marginBottom: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)' }}>
                {user.bio}
              </p>
              <div style={{ marginBottom: 'var(--spacing-3)' }}>
                <strong>Interests:</strong> {user.interests.join(', ')}
              </div>
              <button
                onClick={() => handleRequest(user.id)}
                disabled={requested.has(user.id)}
                className="btn btn--primary"
                style={{ width: '100%' }}
              >
                {requested.has(user.id) ? 'Request Sent' : 'Send Message Request'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Premium Upgrade CTA for Free Users */}
      {isFree && users.length > 3 && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: 'var(--spacing-6)',
          padding: 'var(--spacing-6)',
          background: 'var(--gray-50)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <div style={{ fontSize: '3em', marginBottom: 'var(--spacing-3)' }}>🔒</div>
          <h3 style={{ marginBottom: 'var(--spacing-3)' }}>See More Travel Companions</h3>
          <p style={{ marginBottom: 'var(--spacing-4)', color: 'var(--gray-600)' }}>
            Upgrade to Premium to browse all {users.length} available travelers and start connecting
          </p>
          <Link to="/payment" className="btn btn--primary btn--large">
            Upgrade to Premium
          </Link>
        </div>
      )}

      {/* Premium Teaser Modal */}
      <PremiumTeaser 
        feature="feed"
        showModal={showPremiumTeaser}
        onClose={() => setShowPremiumTeaser(false)}
      />
    </div>
  );
}

